import { throttle } from 'lodash-es';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { translate } from '../../../base/i18n/functions';
import { IconInfo, IconMessage, IconShareDoc, IconSubtitles } from '../../../base/icons/svg';
import { getLocalParticipant, getRemoteParticipants, isPrivateChatEnabledSelf } from '../../../base/participants/functions';
import Tabs from '../../../base/ui/components/web/Tabs';
import { arePollsDisabled } from '../../../conference/functions.any';
import { isFileSharingEnabled } from '../../../file-sharing/functions.any';
import PollsPane from '../../../polls/components/web/PollsPane';
import { isCCTabEnabled } from '../../../subtitles/functions.any';
import { sendMessage, setChatIsResizing, setFocusedTab, setIsPollsTabFocused, setPrivateMessageRecipient, setPrivateMessageRecipientById, setUserChatWidth, toggleChat } from '../../actions.web';
import { OPTION_GROUPCHAT, SMALL_WIDTH_THRESHOLD } from '../../constants';
import { getChatMaxSize, getFocusedTab, isChatDisabled } from '../../functions';
import ChatHeader from './ChatHeader';
import DisplayNameForm from './DisplayNameForm';
import KeyboardAvoider from './KeyboardAvoider';
import { ChatTabs, CHAT_SIZE } from "../../constants";
import { withPixelLineHeight } from "../../../base/styles/functions.web";
import ChatInput from "../../../../11sight/features/chat/components/ChatInput";
import MessageRecipient from "./MessageRecipient";
import MessageContainer from "./MessageContainer";
const useStyles = makeStyles()(theme => {
    return {
        container: {
            backgroundColor: theme.palette.iisightWhiteBackground,
            flexShrink: 0,
            overflow: "hidden",
            position: "relative",
            transition: "width .16s ease-in-out",
            width: `${CHAT_SIZE}px`,
            zIndex: 300,
            "@media (max-width: 580px)": {
                height: "100dvh",
                position: "fixed",
                left: 0,
                right: 0,
                top: 0,
                width: "auto"
            },
            "*": {
                userSelect: "text",
                "-webkit-user-select": "text"
            }
        },
        chatHeader: {
            backgroundColor: theme.palette.iisightBrandBackground,
            height: "60px",
            position: "relative",
            width: "100%",
            zIndex: 1,
            display: "flex",
            justifyContent: "space-between",
            padding: `${theme.spacing(3)} ${theme.spacing(4)}`,
            alignItems: "center",
            boxSizing: "border-box",
            color: theme.palette.text01,
            ...withPixelLineHeight(theme.typography.heading6),
            ".jitsi-icon": {
                cursor: "pointer"
            }
        },
        chatPanel: {
            display: "flex",
            flexDirection: "column",
            height: "calc(100% - 110px)"
        },
        chatPanelNoTabs: {
            height: "calc(100% - 60px)"
        },
        pollsPanel: {
            height: "calc(100% - 110px)"
        }
    };
});
const Chat = ({ _isModal, _isOpen, _isPollsEnabled, _isPollsTabFocused, _isCCTabEnabled, _isChatDisabled, _isFileSharingTabEnabled, _focusedTab, _isResizing, _messages, _nbUnreadMessages, _nbUnreadPolls, _unreadMessagesCount, _unreadPollsCount, _unreadFilesCount, _showNamePrompt, _width, dispatch, t }) => {
    // If no tabs are available, don't render the chat panel at all.
    if (_isChatDisabled && !_isPollsEnabled && !_isCCTabEnabled && !_isFileSharingTabEnabled) {
        return null;
    }
    const { classes, cx } = useStyles({ _isResizing, width: _width });
    const [isMouseDown, setIsMouseDown] = useState(false);
    const [mousePosition, setMousePosition] = useState(null);
    const [dragChatWidth, setDragChatWidth] = useState(null);
    const maxChatWidth = useSelector(getChatMaxSize);
    const notifyTimestamp = useSelector((state) => state['features/chat'].notifyPrivateRecipientsChangedTimestamp);
    const { defaultRemoteDisplayName = 'Fellow Jitster' } = useSelector((state) => state['features/base/config']);
    const privateMessageRecipient = useSelector((state) => state['features/chat'].privateMessageRecipient);
    const participants = useSelector(getRemoteParticipants);
    const isPrivateChatAllowed = useSelector((state) => isPrivateChatEnabledSelf(state));
    const options = useMemo(() => {
        const o = Array.from(participants?.values() || [])
            .filter(p => !p.fakeParticipant)
            .map(p => {
            return {
                value: p.id,
                label: p.name ?? defaultRemoteDisplayName
            };
        });
        o.sort((a, b) => a.label.localeCompare(b.label));
        o.unshift({
            label: t('chat.everyone'),
            value: OPTION_GROUPCHAT
        });
        return o;
    }, [participants, defaultRemoteDisplayName, t, notifyTimestamp]);
    /**
     * Handles mouse down on the drag handle.
     *
     * @param {MouseEvent} e - The mouse down event.
     * @returns {void}
     */
    const onDragHandleMouseDown = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        // Store the initial mouse position and chat width
        setIsMouseDown(true);
        setMousePosition(e.clientX);
        setDragChatWidth(_width);
        // Indicate that resizing is in progress
        dispatch(setChatIsResizing(true));
        // Add visual feedback that we're dragging
        document.body.style.cursor = 'col-resize';
        // Disable text selection during resize
        document.body.style.userSelect = 'none';
    }, [_width, dispatch]);
    /**
     * Drag handle mouse up handler.
     *
     * @returns {void}
     */
    const onDragMouseUp = useCallback(() => {
        if (isMouseDown) {
            setIsMouseDown(false);
            dispatch(setChatIsResizing(false));
            // Restore cursor and text selection
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        }
    }, [isMouseDown, dispatch]);
    /**
     * Handles drag handle mouse move.
     *
     * @param {MouseEvent} e - The mousemove event.
     * @returns {void}
     */
    const onChatResize = useCallback(throttle((e) => {
        if (isMouseDown && mousePosition !== null && dragChatWidth !== null) {
            // For chat panel resizing on the left edge:
            // - Dragging left (decreasing X coordinate) should make the panel wider
            // - Dragging right (increasing X coordinate) should make the panel narrower
            const diff = e.clientX - mousePosition;
            const newWidth = Math.max(Math.min(dragChatWidth + diff, maxChatWidth), CHAT_SIZE);
            // Update the width only if it has changed
            if (newWidth !== _width) {
                dispatch(setUserChatWidth(newWidth));
            }
        }
    }, 50, {
        leading: true,
        trailing: false
    }), [isMouseDown, mousePosition, dragChatWidth, _width, maxChatWidth, dispatch]);
    // Set up event listeners when component mounts
    useEffect(() => {
        document.addEventListener('mouseup', onDragMouseUp);
        document.addEventListener('mousemove', onChatResize);
        return () => {
            document.removeEventListener('mouseup', onDragMouseUp);
            document.removeEventListener('mousemove', onChatResize);
        };
    }, [onDragMouseUp, onChatResize]);
    /**
    * Sends a text message.
    *
    * @private
    * @param {string} text - The text message to be sent.
    * @returns {void}
    * @type {Function}
    */
    const onSendMessage = useCallback((text) => {
        dispatch(sendMessage(text));
    }, []);
    /**
    * Toggles the chat window.
    *
    * @returns {Function}
    */
    const onToggleChat = useCallback(() => {
        dispatch(toggleChat());
    }, []);
    /**
     * Click handler for the chat sidenav.
     *
     * @param {KeyboardEvent} event - Esc key click to close the popup.
     * @returns {void}
     */
    const onEscClick = useCallback((event) => {
        if (event.key === 'Escape' && _isOpen) {
            event.preventDefault();
            event.stopPropagation();
            onToggleChat();
        }
    }, [_isOpen]);
    /**
     * Change selected tab.
     *
     * @param {string} id - Id of the clicked tab.
     * @returns {void}
     */
    const onChangeTab = useCallback((id) => {
        dispatch(setFocusedTab(id));
        // Also update isPollsTabFocused for backward compatibility
        dispatch(setIsPollsTabFocused(id === ChatTabs.POLLS));
    }, [dispatch]);
    const onSelectedRecipientChange = useCallback((e) => {
        const selected = e.target.value;
        if (selected === OPTION_GROUPCHAT) {
            dispatch(setPrivateMessageRecipient());
        }
        else {
            dispatch(setPrivateMessageRecipientById(selected));
        }
    }, [dispatch]);
    function renderChat() {
        return (React.createElement(React.Fragment, null,
            _isPollsEnabled && renderTabs(),
            React.createElement("div", { "aria-labelledby": ChatTabs.CHAT, className: cx(classes.chatPanel, !_isPollsEnabled && classes.chatPanelNoTabs, _isPollsTabFocused && "hide"), id: `${ChatTabs.CHAT}-panel`, role: "tabpanel", tabIndex: 0 },
                React.createElement(MessageContainer, { messages: _messages }),
                React.createElement(MessageRecipient, null),
                React.createElement(ChatInput, { onSend: onSendMessage })),
            _isPollsEnabled && (React.createElement(React.Fragment, null,
                React.createElement("div", { "aria-labelledby": ChatTabs.POLLS, className: cx(classes.pollsPanel, !_isPollsTabFocused && "hide"), id: `${ChatTabs.POLLS}-panel`, role: "tabpanel", tabIndex: 0 },
                    React.createElement(PollsPane, null)),
                React.createElement(KeyboardAvoider, null)))));
    }
    /**
     * Returns a React Element showing the Chat, Polls and Subtitles tabs.
     *
     * @private
     * @returns {ReactElement}
     */
    function renderTabs() {
        // The only way focused tab will be undefined is when no tab is enabled. Therefore this function won't be
        // executed because Chat component won't render anything. This should never happen but adding the check
        // here to make TS happy (when passing the _focusedTab in the selected prop for Tabs).
        if (!_focusedTab) {
            return null;
        }
        let tabs = [];
        // Only add chat tab if chat is not disabled.
        if (!_isChatDisabled) {
            tabs.push({
                accessibilityLabel: t('chat.tabs.chat'),
                countBadge: _focusedTab !== ChatTabs.CHAT && _unreadMessagesCount > 0 ? _unreadMessagesCount : undefined,
                id: ChatTabs.CHAT,
                controlsId: `${ChatTabs.CHAT}-panel`,
                icon: IconMessage,
                title: t('chat.tabs.chat')
            });
        }
        if (_isPollsEnabled) {
            tabs.push({
                accessibilityLabel: t('chat.tabs.polls'),
                countBadge: _focusedTab !== ChatTabs.POLLS && _unreadPollsCount > 0 ? _unreadPollsCount : undefined,
                id: ChatTabs.POLLS,
                controlsId: `${ChatTabs.POLLS}-panel`,
                icon: IconInfo,
                title: t('chat.tabs.polls')
            });
        }
        if (_isCCTabEnabled) {
            tabs.push({
                accessibilityLabel: t('chat.tabs.closedCaptions'),
                countBadge: undefined,
                id: ChatTabs.CLOSED_CAPTIONS,
                controlsId: `${ChatTabs.CLOSED_CAPTIONS}-panel`,
                icon: IconSubtitles,
                title: t('chat.tabs.closedCaptions')
            });
        }
        if (_isFileSharingTabEnabled) {
            tabs.push({
                accessibilityLabel: t('chat.tabs.fileSharing'),
                countBadge: _focusedTab !== ChatTabs.FILE_SHARING && _unreadFilesCount > 0 ? _unreadFilesCount : undefined,
                id: ChatTabs.FILE_SHARING,
                controlsId: `${ChatTabs.FILE_SHARING}-panel`,
                icon: IconShareDoc,
                title: t('chat.tabs.fileSharing')
            });
        }
        if (tabs.length === 1) {
            tabs = [];
        }
        return (React.createElement(Tabs, { accessibilityLabel: _isPollsEnabled || _isCCTabEnabled || _isFileSharingTabEnabled
                ? t('chat.titleWithFeatures', {
                    features: [
                        _isPollsEnabled ? t('chat.titleWithPolls') : '',
                        _isCCTabEnabled ? t('chat.titleWithCC') : '',
                        _isFileSharingTabEnabled ? t('chat.titleWithFileSharing') : ''
                    ].filter(Boolean).join(', ')
                })
                : t('chat.title'), onChange: onChangeTab, selected: _focusedTab, tabs: tabs }));
    }
    return _isOpen ? (React.createElement("div", { className: classes.container, id: "sideToolbarContainer", onKeyDown: onEscClick },
        React.createElement(ChatHeader, { className: cx("chat-header", classes.chatHeader), isPollsEnabled: _isPollsEnabled, onCancel: onToggleChat }),
        _showNamePrompt ? React.createElement(DisplayNameForm, { isPollsEnabled: _isPollsEnabled }) : renderChat())) : null;
};
/**
 * Maps (parts of) the redux state to {@link Chat} React {@code Component}
 * props.
 *
 * @param {Object} state - The redux store/state.
 * @param {any} _ownProps - Components' own props.
 * @private
 * @returns {{
 *     _isModal: boolean,
 *     _isOpen: boolean,
 *     _isPollsEnabled: boolean,
 *     _isPollsTabFocused: boolean,
 *     _isCCTabEnabled: boolean,
 *     _focusedTab: string,
 *     _messages: Array<Object>,
 *     _nbUnreadMessages: number,
 *     _nbUnreadPolls: number,
 *     _unreadMessagesCount: number,
 *     _unreadPollsCount: number,
 *     _unreadFilesCount: number,
 *     _showNamePrompt: boolean,
 *     _width: number,
 *     _isResizing: boolean
 * }}
 */
function _mapStateToProps(state, _ownProps) {
    const { isOpen, isPollsTabFocused, messages, nbUnreadMessages, unreadMessagesCount, unreadFilesCount, width, isResizing } = state['features/chat'];
    const { nbUnreadPolls, unreadPollsCount } = state['features/polls'];
    const _localParticipant = getLocalParticipant(state);
    return {
        _isModal: window.innerWidth <= SMALL_WIDTH_THRESHOLD,
        _isOpen: isOpen,
        _isPollsEnabled: !arePollsDisabled(state),
        _isPollsTabFocused: isPollsTabFocused,
        _isCCTabEnabled: isCCTabEnabled(state),
        _isChatDisabled: isChatDisabled(state),
        _isFileSharingTabEnabled: isFileSharingEnabled(state),
        _focusedTab: getFocusedTab(state),
        _messages: messages,
        _nbUnreadMessages: nbUnreadMessages,
        _nbUnreadPolls: nbUnreadPolls,
        _unreadMessagesCount: unreadMessagesCount || nbUnreadMessages,
        _unreadPollsCount: unreadPollsCount || nbUnreadPolls,
        _unreadFilesCount: unreadFilesCount,
        _showNamePrompt: !_localParticipant?.name,
        _width: width?.current || CHAT_SIZE,
        _isResizing: isResizing
    };
}
export default translate(connect(_mapStateToProps)(Chat));
